import { Navigate, Route, Routes } from "react-router";
import { Callback } from "./auth/Callback";
import { Login } from "./auth/Login";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { AppLayout } from "./components/AppLayout";
import { Home } from "./routes/Home";
import { AiLayout } from "./routes/ai/AiLayout";
import { ClaudeConnection } from "./routes/ai/ClaudeConnection";
import { AiRecordings } from "./routes/logs/AiRecordings";
import { AuditLog } from "./routes/logs/AuditLog";
import { KubernetesRecordings } from "./routes/logs/KubernetesRecordings";
import { LogsLayout } from "./routes/logs/LogsLayout";
import { RdpRecordings } from "./routes/logs/RdpRecordings";
import { SshRecordings } from "./routes/logs/SshRecordings";
import { Profile } from "./routes/Profile";
import { ConnectorDetails } from "./routes/networking/ConnectorDetails";
import { ConnectorLayout } from "./routes/networking/ConnectorLayout";
import { ConnectorResources } from "./routes/networking/ConnectorResources";
import { ConnectorsAndGatewaysLayout } from "./routes/networking/ConnectorsAndGatewaysLayout";
import { ConnectorSettings } from "./routes/networking/ConnectorSettings";
import { Connectors } from "./routes/networking/Connectors";
import { GatewayDetails } from "./routes/networking/GatewayDetails";
import { GatewayLayout } from "./routes/networking/GatewayLayout";
import { GatewayResources } from "./routes/networking/GatewayResources";
import { GatewaySettings } from "./routes/networking/GatewaySettings";
import { Gateways } from "./routes/networking/Gateways";
import { Databases } from "./routes/resources/Databases";
import { KubernetesClusters } from "./routes/resources/KubernetesClusters";
import { ResourcesLayout } from "./routes/resources/ResourcesLayout";
import { VirtualMachines } from "./routes/resources/VirtualMachines";
import { Websites } from "./routes/resources/Websites";
import { RootRedirect } from "./routes/RootRedirect";
import { Encryption } from "./routes/settings/Encryption";
import { LogStreaming } from "./routes/settings/LogStreaming";
import { HostIdentityPolicySettings } from "./routes/settings/HostIdentityPolicySettings";
import { SsoScimSettings } from "./routes/settings/SsoScimSettings";
import { SettingsLayout } from "./routes/settings/SettingsLayout";
import { TeamAccessRules } from "./routes/teams/TeamAccessRules";
import { TeamLayout } from "./routes/teams/TeamLayout";
import { TeamMembers } from "./routes/teams/TeamMembers";
import { TeamSettings } from "./routes/teams/TeamSettings";
import { Teams } from "./routes/Teams";
import { Tokens } from "./routes/Tokens";
import { Users } from "./routes/Users";

export function App() {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <RootRedirect />
          </ProtectedRoute>
        }
      />
      <Route path="/callback" element={<Callback />} />
      <Route path="/login" element={<Login />} />
      <Route element={<AppLayout />}>
        <Route
          path="/:orgId"
          element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          }
        />
        <Route
          path="/:orgId/users"
          element={
            <ProtectedRoute>
              <Users />
            </ProtectedRoute>
          }
        />
        <Route
          path="/:orgId/teams"
          element={
            <ProtectedRoute>
              <Teams />
            </ProtectedRoute>
          }
        />
        <Route
          path="/:orgId/tokens"
          element={
            <ProtectedRoute>
              <Tokens />
            </ProtectedRoute>
          }
        />
        <Route
          path="/:orgId/networking"
          element={
            <ProtectedRoute>
              <ConnectorsAndGatewaysLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="gateways" replace />} />
          <Route path="gateways" element={<Gateways />} />
          <Route path="connectors" element={<Connectors />} />
        </Route>
        <Route
          path="/:orgId/networking/connectors/:connectorId"
          element={
            <ProtectedRoute requiredRole="Admin">
              <ConnectorLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="details" replace />} />
          <Route path="details" element={<ConnectorDetails />} />
          <Route path="resources" element={<ConnectorResources />} />
          <Route path="settings" element={<ConnectorSettings />} />
        </Route>
        <Route
          path="/:orgId/networking/gateways/:gatewayId"
          element={
            <ProtectedRoute requiredRole="Admin">
              <GatewayLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="details" replace />} />
          <Route path="details" element={<GatewayDetails />} />
          <Route path="resources" element={<GatewayResources />} />
          <Route path="settings" element={<GatewaySettings />} />
        </Route>
        <Route
          path="/:orgId/resources"
          element={
            <ProtectedRoute>
              <ResourcesLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="virtual-machines" replace />} />
          <Route path="virtual-machines" element={<VirtualMachines />} />
          <Route path="databases" element={<Databases />} />
          <Route path="websites" element={<Websites />} />
          <Route path="kubernetes-clusters" element={<KubernetesClusters />} />
        </Route>
        <Route
          path="/:orgId/teams/:teamId"
          element={
            <ProtectedRoute>
              <TeamLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="members" replace />} />
          <Route path="members" element={<TeamMembers />} />
          <Route path="access-rules" element={<TeamAccessRules />} />
          <Route
            path="settings"
            element={
              <ProtectedRoute requiredRole="Admin">
                <TeamSettings />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route
          path="/:orgId/logs"
          element={
            <ProtectedRoute requiredRole="Admin">
              <LogsLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="audit" replace />} />
          <Route path="audit" element={<AuditLog />} />
          <Route path="ssh-recordings" element={<SshRecordings />} />
          <Route path="rdp-recordings" element={<RdpRecordings />} />
          <Route
            path="kubernetes-recordings"
            element={<KubernetesRecordings />}
          />
          <Route path="ai-recordings" element={<AiRecordings />} />
        </Route>
        <Route
          path="/:orgId/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/:orgId/ai"
          element={
            <ProtectedRoute requiredRole="Admin">
              <AiLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="claude" replace />} />
          <Route path="claude" element={<ClaudeConnection />} />
        </Route>
        <Route
          path="/:orgId/settings"
          element={
            <ProtectedRoute requiredRole="Admin">
              <SettingsLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="sso" replace />} />
          <Route path="sso" element={<SsoScimSettings />} />
          <Route
            path="host-identity"
            element={<HostIdentityPolicySettings />}
          />
          <Route path="security" element={<Navigate to="../sso" replace />} />
          <Route path="encryption" element={<Encryption />} />
          <Route path="log-streaming" element={<LogStreaming />} />
        </Route>
      </Route>
    </Routes>
  );
}
